// src/AdminHealthDashboard.jsx
//
// Painel admin /admin/health: snapshot agregado de saude do backend para
// detectar problemas como o incidente de 25/05/2026 (no-instance-available
// + OpenAI rate-limit). Faz auto-refresh a cada 30s.
//
// Auth: pede token admin no localStorage ('adminHealthToken'). Sem token,
// mostra prompt para colar. O token e enviado via header x-admin-token e
// validado no backend (env ADMIN_TOKEN).

import React, { useEffect, useMemo, useState, useCallback } from 'react';

const DEFAULT_API_URL =
  process.env.REACT_APP_API_URL ||
  'https://us-central1-consultamaissimplificada.cloudfunctions.net/apiV2';
const BASE_URL = DEFAULT_API_URL.replace(/\/+$/, '');
const REFRESH_INTERVAL_MS = 30000;

function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' });
  } catch {
    return iso;
  }
}

function StatusPill({ status }) {
  const map = {
    ok:    { bg: '#dcfce7', fg: '#166534', label: 'OK' },
    warn:  { bg: '#fef3c7', fg: '#92400e', label: 'ATENÇÃO' },
    error: { bg: '#fee2e2', fg: '#991b1b', label: 'ERRO' },
    unknown: { bg: '#e5e7eb', fg: '#374151', label: 'DESCONHECIDO' },
  };
  const m = map[status] || map.unknown;
  return (
    <span style={{
      background: m.bg, color: m.fg, padding: '4px 10px', borderRadius: 999,
      fontSize: 12, fontWeight: 700, textTransform: 'uppercase',
    }}>{m.label}</span>
  );
}

function Card({ title, status, children }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
      border: '1px solid rgba(0,0,0,0.04)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#111' }}>{title}</h3>
        {status ? <StatusPill status={status} /> : null}
      </div>
      <div style={{ fontSize: 14, color: '#374151' }}>{children}</div>
    </div>
  );
}

const AdminHealthDashboard = () => {
  const [token, setToken] = useState(() => {
    try { return localStorage.getItem('adminHealthToken') || ''; } catch { return ''; }
  });
  const [tokenInput, setTokenInput] = useState('');
  const [snapshot, setSnapshot] = useState(null);
  const [paidApiHealth, setPaidApiHealth] = useState(null);
  const [paidApiLoops, setPaidApiLoops] = useState(null);
  // Saldo estimado da conta OpenAI + estado do fallback de LLM (incidente
  // 31/08/2026: conta sem crédito derrubou a IA do app).
  const [openaiBalance, setOpenaiBalance] = useState(null);
  const [topUpInput, setTopUpInput] = useState('');
  const [topUpMsg, setTopUpMsg] = useState(null);
  // Onda 3E: seção de seguranca (App Check, rate limit, webhooks suspeitos).
  const [securitySnapshot, setSecuritySnapshot] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastFetch, setLastFetch] = useState(null);

  const fetchSnapshot = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const [resHealth, resPaid, resLoops, resSecurity, resBalance] = await Promise.allSettled([
        fetch(`${BASE_URL}/admin/health/snapshot`, { headers: { 'x-admin-token': token } }),
        fetch(`${BASE_URL}/admin/paid-api/health`, { headers: { 'x-admin-token': token } }),
        fetch(`${BASE_URL}/admin/paid-api/loops?cap=2000`, { headers: { 'x-admin-token': token } }),
        fetch(`${BASE_URL}/admin/security/snapshot`, { headers: { 'x-admin-token': token } }),
        fetch(`${BASE_URL}/admin/openai/balance`, { headers: { 'x-admin-token': token } }),
      ]);

      if (resHealth.status === 'fulfilled') {
        const r = resHealth.value;
        if (r.status === 401 || r.status === 403) {
          setError('Token inválido. Limpe e cole o ADMIN_TOKEN correto.');
          setToken('');
          try { localStorage.removeItem('adminHealthToken'); } catch {}
          return;
        }
        if (!r.ok) {
          const txt = await r.text();
          setError(`HTTP ${r.status}: ${txt.slice(0, 200)}`);
        } else {
          setSnapshot(await r.json());
        }
      } else {
        setError(resHealth.reason?.message || String(resHealth.reason));
      }

      if (resPaid.status === 'fulfilled' && resPaid.value.ok) {
        try { setPaidApiHealth(await resPaid.value.json()); } catch {}
      }
      if (resLoops.status === 'fulfilled' && resLoops.value.ok) {
        try { setPaidApiLoops(await resLoops.value.json()); } catch {}
      }
      if (resSecurity.status === 'fulfilled' && resSecurity.value.ok) {
        try { setSecuritySnapshot(await resSecurity.value.json()); } catch {}
      }
      if (resBalance.status === 'fulfilled' && resBalance.value.ok) {
        try { setOpenaiBalance(await resBalance.value.json()); } catch {}
      }

      setLastFetch(new Date().toISOString());
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) return undefined;
    fetchSnapshot();
    const id = setInterval(fetchSnapshot, REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [token, fetchSnapshot]);

  const saveToken = () => {
    const t = (tokenInput || '').trim();
    if (!t) return;
    try { localStorage.setItem('adminHealthToken', t); } catch {}
    setToken(t);
    setTokenInput('');
  };

  // A OpenAI não expõe saldo por API, então a estimativa precisa de um ponto
  // de origem informado à mão. Sem isso o número deriva e o alerta perde valor.
  const submitTopUp = async () => {
    const value = Number(String(topUpInput).replace(',', '.'));
    if (!Number.isFinite(value) || value <= 0) {
      setTopUpMsg('Informe o saldo em dólares (ex.: 250).');
      return;
    }
    setTopUpMsg('Registrando...');
    try {
      const r = await fetch(`${BASE_URL}/admin/openai/balance/topup`, {
        method: 'POST',
        headers: { 'x-admin-token': token, 'Content-Type': 'application/json' },
        body: JSON.stringify({ balanceUSD: value }),
      });
      if (!r.ok) {
        const txt = await r.text();
        setTopUpMsg(`Falhou: HTTP ${r.status} ${txt.slice(0, 120)}`);
        return;
      }
      setTopUpMsg(`Saldo registrado: US$ ${value.toFixed(2)}`);
      setTopUpInput('');
      fetchSnapshot();
    } catch (e) {
      setTopUpMsg(`Falhou: ${e.message || String(e)}`);
    }
  };

  const openaiBalanceStatus = useMemo(() => {
    if (!openaiBalance) return 'unknown';
    if (!openaiBalance.initialized) return 'warn';
    if (openaiBalance.llmFallback && openaiBalance.llmFallback.active) return 'error';
    const tier = openaiBalance.tier;
    if (tier === 'emergency' || tier === 'critical') return 'error';
    if (tier === 'warn' || tier === 'info') return 'warn';
    if (Number(openaiBalance.ledgerAgeDays) > 45) return 'warn';
    return 'ok';
  }, [openaiBalance]);

  const openAiStatus = useMemo(() => {
    if (!snapshot) return 'unknown';
    const oai = snapshot.openai || {};
    if (oai.circuitOpen) return 'error';
    if (oai.status === 'quota') return 'error';
    if (oai.status === 'ok') return 'ok';
    if (oai.status === 'error') return 'warn';
    return 'unknown';
  }, [snapshot]);

  const systemStatus = useMemo(() => {
    if (!snapshot) return 'unknown';
    if (snapshot.systemStatus && snapshot.systemStatus.degradedMode) return 'warn';
    return 'ok';
  }, [snapshot]);

  const orphanStatus = useMemo(() => {
    if (!snapshot || !snapshot.orphanEvents24h) return 'unknown';
    return snapshot.orphanEvents24h.status || 'ok';
  }, [snapshot]);

  const paidDupStatus = useMemo(() => {
    if (!snapshot || !snapshot.paidDupRecovery24h) return 'unknown';
    return snapshot.paidDupRecovery24h.status || 'unknown';
  }, [snapshot]);

  if (!token) {
    return (
      <div style={{ maxWidth: 480, margin: '60px auto', padding: 24, background: '#fff', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
        <h2 style={{ marginTop: 0 }}>Painel admin de saúde</h2>
        <p style={{ color: '#6b7280', fontSize: 14 }}>Cole o ADMIN_TOKEN para acessar o snapshot agregado.</p>
        <input
          type="password"
          value={tokenInput}
          onChange={(e) => setTokenInput(e.target.value)}
          placeholder="ADMIN_TOKEN"
          style={{ width: '100%', padding: '10px 12px', fontSize: 14, border: '1px solid #d1d5db', borderRadius: 8, boxSizing: 'border-box' }}
        />
        <button
          type="button"
          onClick={saveToken}
          style={{ marginTop: 12, width: '100%', background: '#991B1B', color: '#fff', border: 0, padding: '10px 14px', fontSize: 14, fontWeight: 700, borderRadius: 8, cursor: 'pointer' }}
        >Entrar</button>
        {error ? <p style={{ color: '#991b1b', marginTop: 12, fontSize: 13 }}>{error}</p> : null}
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: 24, background: '#F9FAFB', minHeight: '100vh', fontFamily: 'Inter,system-ui,sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24 }}>Saúde do backend (apiV2)</h1>
          <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: 13 }}>
            Última atualização: {formatDateTime(lastFetch)} · auto-refresh 30s
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            onClick={fetchSnapshot}
            disabled={loading}
            style={{ background: '#1f2937', color: '#fff', border: 0, padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: loading ? 'wait' : 'pointer' }}
          >{loading ? 'Atualizando...' : 'Atualizar agora'}</button>
          <button
            type="button"
            onClick={() => { try { localStorage.removeItem('adminHealthToken'); } catch {} setToken(''); }}
            style={{ background: '#fff', color: '#6b7280', border: '1px solid #d1d5db', padding: '8px 14px', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}
          >Sair</button>
        </div>
      </div>

      {error ? (
        <div style={{ background: '#fee2e2', color: '#991b1b', padding: 12, borderRadius: 8, marginBottom: 16 }}>{error}</div>
      ) : null}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
        <Card title="OpenAI" status={openAiStatus}>
          {snapshot ? (
            <div>
              <div><strong>Status:</strong> {snapshot.openai?.status || 'unknown'}</div>
              <div><strong>Circuit aberto:</strong> {snapshot.openai?.circuitOpen ? 'sim (quota/billing)' : 'não'}</div>
              <div><strong>Último OK:</strong> {formatDateTime(snapshot.openai?.lastOkAt)}</div>
              <div><strong>Último modelo:</strong> {snapshot.openai?.lastModel || '—'}</div>
              <div><strong>Última rota:</strong> {snapshot.openai?.lastRoute || '—'}</div>
              {snapshot.openai?.lastError ? (
                <div style={{ marginTop: 8, color: '#991b1b', fontSize: 13 }}>
                  <strong>Último erro:</strong> {snapshot.openai.lastError}
                </div>
              ) : null}
            </div>
          ) : '—'}
        </Card>

        <Card title="Modo de operação" status={systemStatus}>
          {snapshot ? (
            <div>
              <div><strong>Modo degradado:</strong> {snapshot.systemStatus?.degradedMode ? 'SIM' : 'NÃO'}</div>
              {snapshot.systemStatus?.degradedReason ? (
                <div><strong>Motivo:</strong> {snapshot.systemStatus.degradedReason}</div>
              ) : null}
              {snapshot.systemStatus?.degradedSince ? (
                <div><strong>Desde:</strong> {formatDateTime(snapshot.systemStatus.degradedSince)}</div>
              ) : null}
              <p style={{ marginTop: 8, color: '#6b7280', fontSize: 13 }}>
                Toggle: <code>config/system_status</code> em Firestore (campo <code>degradedMode</code>).
              </p>
            </div>
          ) : '—'}
        </Card>

        <Card title="Triagens últimas 24h">
          {snapshot ? (
            <div>
              <div><strong>Pacientes em registration:</strong> {snapshot.triage24h?.totalPartialRegs ?? 0}</div>
              {snapshot.triage24h?.byStep ? (
                <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 13 }}>
                  {Object.entries(snapshot.triage24h.byStep).map(([k, v]) => (
                    <li key={k}>{k}: <strong>{v}</strong></li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : '—'}
        </Card>

        <Card title="Broadcasts (Resend) últimas 72h">
          {snapshot ? (
            <div>
              <div><strong>Total enfileirado:</strong> {snapshot.broadcasts72h?.totalEnqueued ?? 0}</div>
              <div><strong>Com Resend ID (cancelável):</strong> {snapshot.broadcasts72h?.totalWithResendId ?? 0}</div>
              {snapshot.broadcasts72h?.byCampaign ? (
                <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 13 }}>
                  {Object.entries(snapshot.broadcasts72h.byCampaign).map(([id, v]) => (
                    <li key={id}><code>{id}</code>: {v.count} ({v.withResendId} canceláveis)</li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : '—'}
        </Card>

        <Card title="Cloud Run (apiV2)">
          {snapshot ? (
            <div>
              <p style={{ margin: '0 0 8px' }}>{snapshot.cloudRun?.note}</p>
              <a href={snapshot.cloudRun?.consoleUrl} target="_blank" rel="noreferrer" style={{ color: '#991B1B', fontWeight: 600 }}>
                Abrir Cloud Run console ↗
              </a>
              <p style={{ marginTop: 12, color: '#6b7280', fontSize: 13 }}>
                Para alertas críticos, consulte <code>monitoring/README.md</code> no repositório.
              </p>
            </div>
          ) : '—'}
        </Card>

        <Card title="Segurança (24h)" status={securitySnapshot ? 'ok' : 'unknown'}>
          {securitySnapshot ? (
            <div>
              <div>
                <strong>App Check:</strong>{' '}
                {securitySnapshot.appCheck?.mode || 'off'}
                {securitySnapshot.appCheck?.configuredOnFrontend ? '' : ' (frontend sem key)'}
              </div>
              <div>
                <strong>Rate-limit chaves ativas:</strong>{' '}
                {securitySnapshot.rateLimit?.activeKeys24h ?? '—'}
              </div>
              <div>
                <strong>Webhooks suspeitos:</strong>{' '}
                {securitySnapshot.payments?.suspiciousWebhooks24h ?? 0}
              </div>
              <div>
                <strong>Solicitações LGPD pendentes:</strong>{' '}
                {securitySnapshot.lgpd?.deletionRequests24h ?? 0}
              </div>
              <div>
                <strong>Stream UIDs revogados:</strong>{' '}
                {securitySnapshot.stream?.revokedUserIds ?? 0}
              </div>
              <details style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>
                <summary style={{ cursor: 'pointer' }}>Configurações de hardening</summary>
                <ul style={{ margin: '6px 0 0', paddingLeft: 16 }}>
                  <li>ADMIN_TOKEN: {securitySnapshot.hardening?.adminTokenConfigured ? 'set' : 'AUSENTE'}</li>
                  <li>App Check mode: {securitySnapshot.hardening?.appCheckMode || 'off'}</li>
                  <li>Stream JWT TTL: {securitySnapshot.hardening?.streamTokenTtlSec ?? '?'}s</li>
                  <li>MP webhook HMAC: {securitySnapshot.hardening?.mpWebhookHmac ? 'sim' : 'não'}</li>
                  <li>Pagar.me Basic auth: {securitySnapshot.hardening?.pagarmeWebhookBasicAuth ? 'sim' : 'não'}</li>
                  <li>BB webhook Bearer: {securitySnapshot.hardening?.bbWebhookBearer ? 'sim' : 'não'}</li>
                  <li>WhatsApp x-hub-signature: {securitySnapshot.hardening?.waAppSecret ? 'sim' : 'não'}</li>
                </ul>
              </details>
            </div>
          ) : '—'}
        </Card>

        {/* Crédito da conta OpenAI. O saldo é ESTIMADO: a OpenAI não expõe
            saldo por API (não existe endpoint suportado de crédito restante),
            então o cálculo é (recarga registrada − gasto faturado via Admin
            Costs API). Por isso o card mostra a IDADE do registro — número
            velho tem que ficar visível como velho. Cron: openaiBalanceWatchdogV2. */}
        <Card title="Crédito OpenAI (estimado)" status={openaiBalanceStatus}>
          {openaiBalance ? (
            <div>
              {!openaiBalance.initialized ? (
                <div style={{ color: '#92400e', background: '#fffbeb', padding: 10, borderRadius: 8, marginBottom: 10 }}>
                  <strong>Vigia inativo.</strong> Registre o saldo atual da conta OpenAI abaixo — sem
                  isso não há alerta de crédito baixo.
                </div>
              ) : (
                <>
                  <div style={{ fontSize: 28, fontWeight: 800, color: openaiBalanceStatus === 'error' ? '#991b1b' : '#111', marginBottom: 4 }}>
                    {openaiBalance.estimatedBalanceUSD != null
                      ? `US$ ${Number(openaiBalance.estimatedBalanceUSD).toFixed(2)}`
                      : '—'}
                  </div>
                  <div style={{ marginBottom: 8, color: '#6b7280', fontSize: 13 }}>
                    Previsão de zerar:{' '}
                    <strong style={{ color: '#374151' }}>
                      {openaiBalance.etaHours == null
                        ? '—'
                        : openaiBalance.etaHours < 48
                          ? `~${Math.round(openaiBalance.etaHours)}h`
                          : `~${Math.round(openaiBalance.etaHours / 24)} dias`}
                    </strong>
                    {openaiBalance.burn
                      ? ` · consumo ~US$ ${Number(openaiBalance.burn.avgDailyUSD || 0).toFixed(2)}/dia`
                      : ''}
                  </div>
                  <div style={{ marginBottom: 6 }}>
                    <strong>Gasto desde a recarga:</strong>{' '}
                    US$ {Number(openaiBalance.spentSinceTopUpUSD || 0).toFixed(2)} de{' '}
                    US$ {Number(openaiBalance.balanceAtTopUpUSD || 0).toFixed(2)}
                  </div>
                  <div style={{ marginBottom: 8, color: Number(openaiBalance.ledgerAgeDays) > 45 ? '#92400e' : '#6b7280', fontSize: 13 }}>
                    Registro feito há {openaiBalance.ledgerAgeDays} dia(s)
                    {Number(openaiBalance.ledgerAgeDays) > 45
                      ? ' — se houve recarga depois disso, registre de novo: a estimativa está defasada.'
                      : ''}
                  </div>
                </>
              )}

              {openaiBalance.llmFallback ? (
                <div style={{
                  marginBottom: 10, padding: 10, borderRadius: 8, fontSize: 13,
                  background: openaiBalance.llmFallback.active ? '#fef2f2' : '#f9fafb',
                  color: openaiBalance.llmFallback.active ? '#991b1b' : '#374151',
                }}>
                  <strong>Fallback Anthropic:</strong>{' '}
                  {openaiBalance.llmFallback.active
                    ? `ATIVO até ${formatDateTime(openaiBalance.llmFallback.activeUntil)} (${openaiBalance.llmFallback.activatedReason || '—'})`
                    : openaiBalance.llmFallback.enabled
                      ? `armado (clínico: ${openaiBalance.llmFallback.clinicalTasksEnabled ? 'liberado' : 'bloqueado'})`
                      : 'desligado (config/llm_fallback.enabled=false)'}
                  {openaiBalance.llmFallback.forceProvider && openaiBalance.llmFallback.forceProvider !== 'auto'
                    ? ` · forçado: ${openaiBalance.llmFallback.forceProvider}`
                    : ''}
                </div>
              ) : null}

              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
                <input
                  type="text"
                  value={topUpInput}
                  onChange={(e) => setTopUpInput(e.target.value)}
                  placeholder="Saldo atual em US$"
                  style={{ flex: 1, padding: '8px 10px', fontSize: 13, border: '1px solid #d1d5db', borderRadius: 8, boxSizing: 'border-box' }}
                />
                <button
                  type="button"
                  onClick={submitTopUp}
                  style={{ background: '#991B1B', color: '#fff', border: 0, padding: '8px 14px', fontSize: 13, fontWeight: 700, borderRadius: 8, cursor: 'pointer' }}
                >Registrar recarga</button>
              </div>
              {topUpMsg ? <p style={{ margin: '8px 0 0', fontSize: 12, color: '#6b7280' }}>{topUpMsg}</p> : null}
              <p style={{ margin: '10px 0 0', fontSize: 11, color: '#9ca3af' }}>
                As faixas de saldo (US$100, US$50, US$25, US$10 ou ETA &lt; 24h) ficam só no histórico, sem e-mail.
              </p>
            </div>
          ) : '—'}
        </Card>

        {/* APIs pagas não-OpenAI: Resend/Prescreve/Memed/gateways/Stream/Ably/FCM.
            Guardrails 100% passivos (só alertas + logs). Dados via
            GET /admin/paid-api/health + /loops. Destino dos alertas em
            config/paid_api_guard.alertEmail (default gui.esmanhotto@gmail.com). */}
        <Card
          title="APIs Pagas (última hora)"
          status={paidApiHealth ? (paidApiHealth.loopAlertCountLastHour > 0 ? 'warn' : 'ok') : 'unknown'}
        >
          {paidApiHealth ? (
            <div>
              <div style={{ marginBottom: 6 }}>
                <strong>Alertas:</strong> {paidApiHealth.loopAlertCountLastHour || 0} loop(s) ·
                {' '}{paidApiHealth.errorCountLastHour || 0} erro(s)
              </div>
              <div>
                <strong>Custo estimado:</strong>{' '}
                ${Number(paidApiHealth.costUSDLastHour || 0).toFixed(4)} USD
                {paidApiHealth.costBRLLastHour ? ` · R$${Number(paidApiHealth.costBRLLastHour).toFixed(2)} BRL` : ''}
              </div>
              <div>
                <strong>Total chamadas:</strong> {paidApiHealth.totalLastHour || 0}
              </div>
              <table style={{ width: '100%', fontSize: 12, marginTop: 10, borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: '#6b7280' }}>
                    <th style={{ padding: '4px 0' }}>API</th>
                    <th style={{ padding: '4px 4px', textAlign: 'right' }}>1h</th>
                    <th style={{ padding: '4px 4px', textAlign: 'right' }}>base/h</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(paidApiHealth.perApi || {})
                    .sort((a, b) => (b[1].callsLastHour || 0) - (a[1].callsLastHour || 0))
                    .map(([api, v]) => {
                      const cur = v.callsLastHour || 0;
                      const base = v.baselineCallsPerHour || 0;
                      const factor = v.anomalyFactor || 3;
                      const floor = v.anomalyFloorPerHour || 0;
                      const threshold = Math.max(floor, factor * base);
                      const breached = threshold > 0 && cur >= threshold;
                      const disabledColor = v.disabled ? '#9ca3af' : (breached ? '#92400e' : '#374151');
                      return (
                        <tr key={api} style={{ borderTop: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '4px 0', fontFamily: 'monospace', color: disabledColor }}>
                            {api}{v.disabled ? ' (off)' : ''}
                          </td>
                          <td style={{ padding: '4px 4px', textAlign: 'right', fontWeight: breached ? 700 : 400, color: disabledColor }}>
                            {cur}
                          </td>
                          <td style={{ padding: '4px 4px', textAlign: 'right', color: '#6b7280' }}>
                            {base ? base.toFixed(1) : '—'}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
              <p style={{ marginTop: 8, color: '#6b7280', fontSize: 11 }}>
                Destino dos alertas: <code>{paidApiHealth.alertEmail || 'gui.esmanhotto@gmail.com'}</code>.{' '}
                Override: <code>config/paid_api_guard.alertEmail</code>.
              </p>
            </div>
          ) : '—'}
        </Card>

        <Card
          title="Loops APIs Pagas (24h)"
          status={paidApiLoops && paidApiLoops.count > 0 ? 'warn' : 'ok'}
        >
          {paidApiLoops ? (
            paidApiLoops.count > 0 ? (
              <div>
                <div style={{ marginBottom: 8 }}>
                  <strong>{paidApiLoops.count}</strong> dedupHash com ≥2 ocorrências (top 100).
                </div>
                <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ textAlign: 'left', color: '#6b7280' }}>
                      <th style={{ padding: '4px 0' }}>API/ação</th>
                      <th style={{ padding: '4px 4px', textAlign: 'right' }}>chamadas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(paidApiLoops.loops || []).slice(0, 8).map((l, idx) => (
                      <tr key={idx} style={{ borderTop: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '4px 0', fontFamily: 'monospace' }}>
                          {l.api}/{l.action}
                        </td>
                        <td style={{ padding: '4px 4px', textAlign: 'right', fontWeight: 700 }}>
                          {l.count}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p style={{ marginTop: 8, color: '#6b7280', fontSize: 11 }}>
                  Detalhes: <code>GET /admin/paid-api/loops?api=&lt;api&gt;</code>.
                </p>
              </div>
            ) : (
              <div style={{ color: '#374151' }}>Nenhum loop com ≥2 ocorrências no período.</div>
            )
          ) : '—'}
        </Card>

        {/* Eventos órfãos do fluxo cliente: sinais de degradação silenciosa
            (perda de patientId, finalize bloqueado, recovery exauriu retries
            etc). Antes do fix de 04/06/2026 eram logados mas ninguém olhava
            — incidentes Beatriz/Sara/Raíssa só viraram tickets após reclamação. */}
        <Card title="Eventos órfãos (24h)" status={orphanStatus}>
          {snapshot && snapshot.orphanEvents24h ? (
            <div>
              <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: '#6b7280' }}>
                    <th style={{ padding: '4px 0' }}>Evento</th>
                    <th style={{ padding: '4px 8px', textAlign: 'right' }}>Total</th>
                    <th style={{ padding: '4px 0', width: 80 }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(snapshot.orphanEvents24h.events || []).map((e) => {
                    const color = e.status === 'error' ? '#991b1b'
                      : e.status === 'warn' ? '#92400e' : '#374151';
                    return (
                      <tr key={e.name} style={{ borderTop: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '6px 0', color, fontFamily: 'monospace', fontSize: 12 }}>
                          {e.name}
                        </td>
                        <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color }}>
                          {e.count}
                        </td>
                        <td style={{ padding: '6px 0' }}>
                          <StatusPill status={e.status} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p style={{ marginTop: 12, color: '#6b7280', fontSize: 11 }}>
                Eventos &gt; threshold warn/error indicam degradação do fluxo do cliente.
                Detalhes em <code>functions/index.js</code> (ORPHAN_THRESHOLDS).
              </p>
            </div>
          ) : '—'}
        </Card>

        {/* Deduplicação silenciosa de partial pago (caso Cristiane 06/06/2026).
            Mede quantos pacientes reentraram no funil/chat e foram automaticamente
            direcionados ao chat pago existente, evitando duplicação.
            - autoRecovered: lookup em EmailConfirmForm/EmailForm achou paid e adotou.
            - bootRecovered: ConsultaChat reidratou sessao por identidade (cross-device).
            - blockedAtBackend: /registration/start retornou 409 (defesa final).
            - noRecovery: bootstrap esgotou todas as camadas (degradacao). */}
        <Card title="Dedup chat pago (24h)" status={paidDupStatus}>
          {snapshot && snapshot.paidDupRecovery24h ? (
            <div>
              <div style={{ fontSize: 13 }}>
                <div><strong>Adotados no funil (auto):</strong> {snapshot.paidDupRecovery24h.autoRecovered}</div>
                <div><strong>Recuperados no chat boot:</strong> {snapshot.paidDupRecovery24h.bootRecovered}</div>
                <div><strong>Bloqueados no backend (409):</strong> {snapshot.paidDupRecovery24h.blockedAtBackend}</div>
                <div style={{
                  color: snapshot.paidDupRecovery24h.noRecovery > 0 ? '#92400e' : '#374151',
                  fontWeight: snapshot.paidDupRecovery24h.noRecovery > 0 ? 600 : 400,
                }}>
                  <strong>Sem recovery (caiu pra Home):</strong> {snapshot.paidDupRecovery24h.noRecovery}
                </div>
              </div>
              <p style={{ marginTop: 12, color: '#6b7280', fontSize: 11 }}>
                Defesa contra duplicação de chat após perda de localStorage.
                Janela de 48h. autoRecovered/bootRecovered ≥ noRecovery indica defesa saudável.
              </p>
            </div>
          ) : '—'}
        </Card>
      </div>

      {snapshot ? (
        <details style={{ marginTop: 20, background: '#fff', borderRadius: 12, padding: 16, fontSize: 12 }}>
          <summary style={{ cursor: 'pointer', fontWeight: 600 }}>JSON bruto</summary>
          <pre style={{ overflow: 'auto', marginTop: 8 }}>{JSON.stringify(snapshot, null, 2)}</pre>
        </details>
      ) : null}
    </div>
  );
};

export default AdminHealthDashboard;
